import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FEATURE_IMAGES: Record<string, string> = {
  finds: 'https://readdy.ai/api/search-image?query=A%20bright%20organized%20flat-lay%20of%20assorted%20fashion%20products%20including%20shoes%2C%20a%20hoodie%2C%20a%20cap%20and%20accessories%20arranged%20on%20a%20soft%20mint%20green%20background%2C%20clean%20cheerful%20editorial%20product%20photography%2C%20fresh%20aesthetic%2C%20high%20detail&width=600&height=400&seq=usfans-feature-finds&orientation=landscape&nocache=true',
  qc: 'https://readdy.ai/api/search-image?query=A%20close-up%20of%20a%20person%20inspecting%20a%20sneaker%20with%20a%20magnifying%20glass%20on%20a%20light%20worktable%2C%20quality%20control%20scene%2C%20bright%20clean%20workshop%20lighting%2C%20detailed%20editorial%20photography&width=600&height=400&seq=usfans-feature-qc&orientation=landscape&nocache=true',
  shipping: 'https://readdy.ai/api/search-image?query=A%20neat%20stack%20of%20brown%20cardboard%20parcel%20boxes%20with%20a%20shipping%20label%20and%20tape%20on%20a%20warm%20beige%20background%2C%20logistics%20ecommerce%20concept%2C%20soft%20studio%20light%2C%20clean%20product%20photography&width=600&height=400&seq=usfans-feature-shipping&orientation=landscape&nocache=true',
  sellers: 'https://readdy.ai/api/search-image?query=A%20stylish%20clothing%20boutique%20storefront%20with%20neatly%20displayed%20garments%20on%20racks%20and%20shelves%2C%20warm%20inviting%20interior%20lighting%2C%20editorial%20retail%20photography%2C%20high%20detail&width=600&height=400&seq=usfans-feature-sellers&orientation=landscape&nocache=true',
};

const FEATURES = [
  { key: 'finds', icon: 'ri-search-eye-line', image: FEATURE_IMAGES.finds },
  { key: 'qc', icon: 'ri-shield-check-line', image: FEATURE_IMAGES.qc },
  { key: 'shipping', icon: 'ri-truck-line', image: FEATURE_IMAGES.shipping },
  { key: 'sellers', icon: 'ri-store-2-line', image: FEATURE_IMAGES.sellers },
];

export default function ContentSections() {
  const { t } = useTranslation();

  return (
    <>
      {/* What is USFans Spreadsheet */}
      <section id="what" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl mb-6">
                {t('what.title')}
              </h2>
              <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                <p>{t('what.p1')}</p>
                <p>{t('what.p2')}</p>
                <p>{t('what.p3')}</p>
              </div>
              <Link
                to="/usfans-spreadsheet-guide"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                {t('what.cta')}
                <span className="flex items-center justify-center w-5 h-5">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {FEATURES.map((item) => (
                <div
                  key={item.key}
                  className="flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                >
                  <div className="w-full h-24 md:h-28 overflow-hidden bg-background-100">
                    <img
                      src={item.image}
                      alt={t(`what.${item.key}.title`)}
                      title={`${t(`what.${item.key}.title`)} · USFans Spreadsheet`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col gap-1 p-4">
                    <span className="flex items-center gap-2 text-primary-600">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100">
                        <i className={`${item.icon} text-sm`} />
                      </span>
                      <span className="font-heading font-semibold text-foreground-900 text-sm whitespace-nowrap">
                        {t(`what.${item.key}.title`)}
                      </span>
                    </span>
                    <p className="text-foreground-600 text-xs leading-relaxed">
                      {t(`what.${item.key}.desc`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guide card */}
      <section id="guide" className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-8">
          <div className="flex-1">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold mb-4 whitespace-nowrap">
              {t('guideCard.readTime')}
            </span>
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-3xl mb-3">
              {t('guideCard.title')}
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              {t('guideCard.summary')}
            </p>
          </div>
          <Link
            to="/usfans-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0"
          >
            {t('guideCard.cta')}
            <span className="flex items-center justify-center w-5 h-5">
              <i className="ri-arrow-right-line text-base" />
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}